import React, { useState, useMemo, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
  CircularProgress,
  InputAdornment,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Collapse,
  Divider,
  Alert,
  Switch,
} from "@mui/material";
import PersonSearchRoundedIcon from "@mui/icons-material/PersonSearchRounded";
import PersonAddRoundedIcon from "@mui/icons-material/PersonAddRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded";
import { MIN_CLIENT_SEARCH_LENGTH, useClients } from "../../hooks/useClients";
import { useTypes } from "../../hooks/useTypes";
import { useChannels } from "../../hooks/useChannels";
import { useUsers } from "../../hooks/useUsers";
import { useAgents } from "../../hooks/useAgents";
import { useCompanies } from "../../hooks/useCompanies";
import useDebouncedValue from "../../hooks/useDebouncedValue";
import { checkOpenConversationsByPhone, apifacilGroupAdmin } from "../../api/client";
import { useTranslation } from "react-i18next";
import { getSelectedCompanyIds } from "../../utils/workspaceStorage";
import ClientSearchOption from "../../components/clients/ClientSearchOption";
import WhatsappGroupParticipantsPicker from "../../components/clients/WhatsappGroupParticipantsPicker";
import { isWhatsAppQrChannelTipo } from "../../constants/channelCatalog";
import { whatsappQrCapabilities } from "../../utils/whatsappQrCapabilities";
import { extractCreatedWhatsappGroupJid } from "../../utils/whatsappQrGroupMentions";
import { clientWhatsappPhoneDigits } from "../../utils/phone";

const isChatBotUser = (u) => Number(u?.ura) === 1;

/** Achata a árvore de tipos (API retorna tree=1 com children); define id_pai nos filhos. */
function flattenTypesTree(nodes, parentId = null) {
  if (!Array.isArray(nodes)) return [];
  return nodes.flatMap((n) => {
    const id = n.id;
    const idPai =
      n.id_pai != null && n.id_pai !== "" ? Number(n.id_pai) : parentId;
    const nome = n.nome ?? n.name ?? String(id);
    const { children: _c, ...rest } = n;
    const item = { ...rest, id: Number(id), id_pai: idPai, nome };
    return [
      item,
      ...flattenTypesTree(n.children || [], Number(id)),
    ];
  });
}

/**
 * Modal para criar nova solicitação no Chat.
 * - Não permite criar em branco: exige cliente existente ou novo cadastro.
 * - Opções avançadas: tipo, subtipos, origem (canal), responsável (default usuário logado).
 *
 * @param {boolean} open
 * @param {() => void} onClose
 * @param {(payload: object) => Promise<any>} onCreate
 * @param {boolean} isCreating
 * @param {{ id?: number; nome?: string; name?: string }} currentUser - usuário logado (default para responsável)
 */

const NewRequestModal = ({ open, onClose, onCreate, isCreating, currentUser }) => {
  const { t } = useTranslation("chat");
  const [clientMode, setClientMode] = useState("existing"); // "existing" | "new"
  const [clientSearch, setClientSearch] = useState("");
  const [selectedClient, setSelectedClient] = useState(null);
  const [newClientName, setNewClientName] = useState("");
  const [newClientEmail, setNewClientEmail] = useState("");
  const [newClientPhone, setNewClientPhone] = useState("");

  const [typeId, setTypeId] = useState("");
  const [subtype1, setSubtype1] = useState("");
  const [subtype2, setSubtype2] = useState("");
  const [subtype3, setSubtype3] = useState("");
  const [channelId, setChannelId] = useState("");
  const [assigneeType, setAssigneeType] = useState("user"); // "queue" | "user" | "agent"
  const [assigneeId, setAssigneeId] = useState("");
  const [initialMessage, setInitialMessage] = useState("");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [createWhatsappGroup, setCreateWhatsappGroup] = useState(false);
  const [groupSubject, setGroupSubject] = useState("");
  const [groupParticipantClients, setGroupParticipantClients] = useState([]);
  const [groupParticipantSearch, setGroupParticipantSearch] = useState("");
  const [creatingGroup, setCreatingGroup] = useState(false);
  const [groupCreateError, setGroupCreateError] = useState("");
  /** Quando há mais de uma empresa selecionada, id da empresa escolhida para vincular (id_projeto). */
  const [selectedProjectId, setSelectedProjectId] = useState("");

  // Validação prévia de solicitações abertas por telefone
  const [isCheckingOpenByPhone, setIsCheckingOpenByPhone] = useState(false);
  const [openPhoneWarning, setOpenPhoneWarning] = useState(false);
  const [openPhoneWarningItems, setOpenPhoneWarningItems] = useState([]);
  const [pendingCreatePayload, setPendingCreatePayload] = useState(null);

  // Ler do sessionStorage em todo render quando o modal está aberto (valor sempre atualizado do header)
  const selectedCompanyIds = open ? getSelectedCompanyIds() : [];
  const hasOneCompany = selectedCompanyIds.length === 1;
  const hasMultipleCompanies = selectedCompanyIds.length > 1;
  const hasNoCompany = selectedCompanyIds.length === 0;

  // Empresa efetiva do form: uma = essa; várias = a escolhida no select (para vincular cliente e listar só os da empresa)
  const effectiveProjectId =
    hasOneCompany
      ? Number(selectedCompanyIds[0])
      : hasMultipleCompanies && selectedProjectId
      ? Number(selectedProjectId)
      : null;

  const debouncedSearch = useDebouncedValue(clientSearch, 400);
  const { data: clientsData, isLoading: loadingClients } = useClients(
    1,
    15,
    debouncedSearch.trim(),
    effectiveProjectId ?? undefined,
    {
      // evita COUNT pesado quando modal está fechado / sem termo
      enabled:
        !!open &&
        clientMode === "existing" &&
        typeof debouncedSearch === "string" &&
        debouncedSearch.trim().length >= MIN_CLIENT_SEARCH_LENGTH &&
        // quando há múltiplas empresas, só busca após selecionar uma
        (!hasMultipleCompanies || !!selectedProjectId),
    }
  );
  const clients = clientsData?.clients ?? [];
  const debouncedGroupSearch = useDebouncedValue(groupParticipantSearch, 400);
  const { data: groupClientsData, isLoading: loadingGroupClients } = useClients(
    1,
    15,
    debouncedGroupSearch.trim(),
    effectiveProjectId ?? undefined,
    {
      enabled:
        !!open &&
        createWhatsappGroup &&
        typeof debouncedGroupSearch === "string" &&
        debouncedGroupSearch.trim().length >= MIN_CLIENT_SEARCH_LENGTH &&
        (!hasMultipleCompanies || !!selectedProjectId),
    }
  );
  const groupClientOptions = groupClientsData?.clients ?? [];

  const { data: companiesData } = useCompanies(1, 100, "");
  const allCompanies = companiesData?.companies ?? [];
  const companiesForSelect = useMemo(
    () => allCompanies.filter((c) => selectedCompanyIds.includes(Number(c.id))),
    [allCompanies, selectedCompanyIds]
  );
  // Opções para o select: nomes da API quando disponível; senão fallback por id (Empresa 21, Empresa 22)
  const companySelectOptions = useMemo(() => {
    if (companiesForSelect.length > 0) {
      return companiesForSelect.map((c) => ({ id: Number(c.id), label: c.nome ?? c.name ?? `Empresa ${c.id}` }));
    }
    return selectedCompanyIds.map((id) => ({ id, label: `Empresa ${id}` }));
  }, [companiesForSelect, selectedCompanyIds]);

  const { data: typesData } = useTypes(1, 500, "");
  const typesFlat = useMemo(
    () => flattenTypesTree(typesData?.types ?? []),
    [typesData?.types]
  );
  const rootTypes = useMemo(
    () =>
      typesFlat.filter((t) => t.id_pai == null || t.id_pai === 0),
    [typesFlat]
  );
  const subtype1Options = useMemo(
    () => (typeId ? typesFlat.filter((t) => Number(t.id_pai) === Number(typeId)) : []),
    [typesFlat, typeId]
  );
  const subtype2Options = useMemo(
    () =>
      subtype1
        ? typesFlat.filter((t) => Number(t.id_pai) === Number(subtype1))
        : [],
    [typesFlat, subtype1]
  );
  const subtype3Options = useMemo(
    () =>
      subtype2
        ? typesFlat.filter((t) => Number(t.id_pai) === Number(subtype2))
        : [],
    [typesFlat, subtype2]
  );

  const { data: channelsData } = useChannels(1, 200, "");
  const channels = channelsData?.channels ?? [];
  const selectedChannel = useMemo(
    () => channels.find((ch) => String(ch.id) === String(channelId)) || null,
    [channels, channelId]
  );
  const canCreateWhatsappGroup = useMemo(() => {
    if (!selectedChannel || !isWhatsAppQrChannelTipo(selectedChannel.tipo)) return false;
    return Boolean(
      whatsappQrCapabilities(
        selectedChannel.whatsapp_qr_api_version || selectedChannel.whatsappQrApiVersion
      ).groupAdmin
    );
  }, [selectedChannel]);
  const groupParticipantPhones = useMemo(() => {
    const seen = new Set();
    const phones = [];
    for (const client of groupParticipantClients) {
      const digits = clientWhatsappPhoneDigits(client);
      if (!digits || digits.length < 8 || seen.has(digits)) continue;
      seen.add(digits);
      phones.push(digits);
    }
    return phones;
  }, [groupParticipantClients]);

  useEffect(() => {
    if (!canCreateWhatsappGroup && createWhatsappGroup) {
      setCreateWhatsappGroup(false);
      setGroupCreateError("");
    }
  }, [canCreateWhatsappGroup, createWhatsappGroup]);

  // Precisa incluir ChatBots (ura=1) para permitir atribuição/transferência ao bot.
  const { data: usersData } = useUsers(1, 1000, "", "todos", { order: "nome" });
  const users = usersData?.users ?? [];
  const { data: agentsData } = useAgents(1, 200, "");
  const agents = agentsData?.agents ?? [];
  const currentUserId = currentUser?.id ?? null;

  const humanUsers = (users || []).filter((u) => !isChatBotUser(u));
  const chatbotUsers = (users || []).filter(isChatBotUser);

  useEffect(() => {
    if (open && currentUserId && assigneeType === "user" && !assigneeId) {
      setAssigneeId(String(currentUserId));
    }
  }, [open, currentUserId, assigneeType, assigneeId]);

  useEffect(() => {
    if (open && hasMultipleCompanies && selectedProjectId && !selectedCompanyIds.includes(Number(selectedProjectId))) {
      setSelectedProjectId("");
    }
  }, [open, hasMultipleCompanies, selectedProjectId, selectedCompanyIds]);

  // Ao trocar a empresa (quando há várias), limpar cliente selecionado para forçar escolha da lista filtrada pela nova empresa
  const prevSelectedProjectIdRef = React.useRef(selectedProjectId);
  useEffect(() => {
    if (hasMultipleCompanies && prevSelectedProjectIdRef.current !== selectedProjectId) {
      prevSelectedProjectIdRef.current = selectedProjectId;
      setSelectedClient(null);
      setClientSearch("");
    } else if (hasMultipleCompanies) {
      prevSelectedProjectIdRef.current = selectedProjectId;
    }
  }, [hasMultipleCompanies, selectedProjectId]);

  useEffect(() => {
    if (!typeId) {
      setSubtype1("");
      setSubtype2("");
      setSubtype3("");
    }
  }, [typeId]);
  useEffect(() => {
    if (!subtype1) setSubtype2("");
    if (!subtype2) setSubtype3("");
  }, [subtype1, subtype2]);

  const handleClose = () => {
    if (!isCreating && !creatingGroup) {
      setClientMode("existing");
      setClientSearch("");
      setSelectedClient(null);
      setNewClientName("");
      setNewClientEmail("");
      setNewClientPhone("");
      setTypeId("");
      setSubtype1("");
      setSubtype2("");
      setSubtype3("");
      setChannelId("");
      setAssigneeType("user");
      setAssigneeId(currentUserId ? String(currentUserId) : "");
      setInitialMessage("");
      setShowAdvanced(false);
      setCreateWhatsappGroup(false);
      setGroupSubject("");
      setGroupParticipantClients([]);
      setGroupParticipantSearch("");
      setCreatingGroup(false);
      setGroupCreateError("");
      setSelectedProjectId("");
      setIsCheckingOpenByPhone(false);
      setOpenPhoneWarning(false);
      setOpenPhoneWarningItems([]);
      setPendingCreatePayload(null);
      onClose();
    }
  };

  const selectedType = useMemo(
    () => (typeId ? typesFlat.find((t) => Number(t.id) === Number(typeId)) : null),
    [typesFlat, typeId]
  );
  // Só deriva typeKind quando o usuário escolheu um tipo; senão a API usa defaults do canal/tipo
  const derivedTypeKind =
    selectedType == null ? undefined : selectedType.tipo === 0 ? "0" : "1";

  const canSubmit = () => {
    if (hasNoCompany) return false;
    if (hasMultipleCompanies && !selectedProjectId) return false;
    if (isCreating || creatingGroup) return false;
    if (createWhatsappGroup && canCreateWhatsappGroup) {
      return Boolean(channelId) && groupSubject.trim().length >= 2 && groupParticipantPhones.length >= 1;
    }
    if (clientMode === "existing") {
      return !!selectedClient?.id;
    }
    const name = (newClientName || "").trim();
    return name.length >= 2;
  };

  const buildPayload = () => {
    const projectId = hasOneCompany
      ? Number(selectedCompanyIds[0])
      : hasMultipleCompanies && selectedProjectId
      ? Number(selectedProjectId)
      : null;

    let clientPayload;
    let clientIdPayload;
    if (clientMode === "existing" && selectedClient?.id) {
      clientIdPayload = selectedClient.id;
      clientPayload = { id: selectedClient.id };
    } else {
      const name = (newClientName || "").trim() || "Cliente";
      clientPayload = {
        name,
        email: (newClientEmail || "").trim() || undefined,
        phone: (newClientPhone || "").trim() || undefined,
        telephone: (newClientPhone || "").trim() || undefined,
        ...(projectId != null && { id_projeto: projectId }),
      };
    }

    return {
      projectId: projectId || undefined,
      client: clientPayload,
      ...(clientIdPayload != null && clientIdPayload !== "" && { clientId: clientIdPayload }),
      ...(derivedTypeKind != null && { typeKind: derivedTypeKind }),
      typeId: typeId ? Number(typeId) : undefined,
      subtype1: subtype1 ? Number(subtype1) : undefined,
      subtype2: subtype2 ? Number(subtype2) : undefined,
      subtype3: subtype3 ? Number(subtype3) : undefined,
      channelId: channelId ? Number(channelId) : undefined,
      assignedUserId: assigneeType === "user" && assigneeId ? Number(assigneeId) : undefined,
      assignedAgentId: assigneeType === "agent" && assigneeId ? Number(assigneeId) : undefined,
      initialMessage: (initialMessage || "").trim() || "",
      source: "chat",
    };
  };

  const getFormPhoneForCheck = () => {
    if (clientMode === "existing") {
      const c = selectedClient || {};
      return (
        c.phone ||
        c.celular ||
        c.telefone ||
        ""
      );
    }
    return (newClientPhone || "").trim();
  };

  const doCreateConversation = async (payload) => {
    try {
      await onCreate(payload);
      handleClose();
    } catch (_) {
      // erro tratado no pai (Snackbar)
    }
  };

  const handleSubmit = async () => {
    if (!canSubmit()) return;

    if (createWhatsappGroup && canCreateWhatsappGroup) {
      setGroupCreateError("");
      setCreatingGroup(true);
      try {
        const created = await apifacilGroupAdmin(channelId, "create", {
          subject: groupSubject.trim(),
          participants: groupParticipantPhones,
        });
        const groupJid = extractCreatedWhatsappGroupJid(created);
        if (!groupJid) {
          setGroupCreateError(t("newRequest.groupIdMissing"));
          return;
        }
        const instanceId = String(
          selectedChannel?.numerowhatsapp || selectedChannel?.instanceId || ""
        ).trim();
        const payload = {
          ...buildPayload(),
          channelId: Number(channelId),
          client: {
            name: groupSubject.trim(),
            internalId: `apifacil-group:${instanceId || "0"}:${groupJid}`,
            phone: "",
          },
        };
        delete payload.clientId;
        await doCreateConversation(payload);
      } catch (err) {
        setGroupCreateError(err?.message || t("newRequest.groupCreateFailed"));
      } finally {
        setCreatingGroup(false);
      }
      return;
    }

    const payload = buildPayload();
    const phoneToCheck = getFormPhoneForCheck();
    const trimmedPhone = String(phoneToCheck || "").trim();

    // Se não há telefone, não há como validar duplicidade por número: segue direto
    if (!trimmedPhone) {
      await doCreateConversation(payload);
      return;
    }

    try {
      setIsCheckingOpenByPhone(true);
      const projectId = payload.projectId != null ? Number(payload.projectId) : null;
      const existing = await checkOpenConversationsByPhone({
        phone: trimmedPhone,
        projectId: projectId || undefined,
      });

      if (Array.isArray(existing) && existing.length > 0) {
        setOpenPhoneWarningItems(existing);
        setPendingCreatePayload(payload);
        setOpenPhoneWarning(true);
        return;
      }

      await doCreateConversation(payload);
    } catch (err) {
      // Em caso de erro na checagem, não bloqueia a criação
      await doCreateConversation(payload);
    } finally {
      setIsCheckingOpenByPhone(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t("newRequest.title")}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 0.5 }}>
          {/* Empresa: obrigatório; uma = vínculo automático; várias = usuário escolhe */}
          {hasNoCompany && (
            <Typography variant="body2" color="error" sx={{ py: 1 }}>
              {t("newRequest.selectCompanyError")}
            </Typography>
          )}
          {hasMultipleCompanies && (
            <Box>
              <FormControl fullWidth size="small" required>
                <InputLabel>{t("newRequest.linkCompany")}</InputLabel>
                <Select
                  value={selectedProjectId}
                  label={t("newRequest.linkCompany")}
                  onChange={(e) => setSelectedProjectId(String(e.target.value ?? ""))}
                >
                  <MenuItem value="">
                    <em>{t("newRequest.selectCompany")}</em>
                  </MenuItem>
                  {companySelectOptions.map((opt) => (
                    <MenuItem key={opt.id} value={String(opt.id)}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}

          {/* Cliente: existente ou novo cadastro; quando várias empresas, vincular à empresa selecionada */}
          <Box sx={{ display: createWhatsappGroup && canCreateWhatsappGroup ? "none" : "block" }}>
            {hasMultipleCompanies && !selectedProjectId && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {t("newRequest.selectCompanyForClients")}
              </Typography>
            )}
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              {t("newRequest.client")}
            </Typography>
            <RadioGroup
              row
              value={clientMode}
              onChange={(e) => setClientMode(e.target.value)}
              sx={{ mb: 1.5 }}
            >
              <FormControlLabel
                value="existing"
                control={<Radio size="small" />}
                label={t("newRequest.searchExisting")}
              />
              <FormControlLabel
                value="new"
                control={<Radio size="small" />}
                label={t("newRequest.registerNew")}
              />
            </RadioGroup>

            {clientMode === "existing" ? (
              <Autocomplete
                options={clients}
                getOptionLabel={(option) =>
                  typeof option === "object" ? option?.name || option?.nome || "" : ""
                }
                value={selectedClient}
                onChange={(_, value) => setSelectedClient(value)}
                inputValue={clientSearch}
                onInputChange={(_, value) => setClientSearch(value)}
                loading={loadingClients}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("newRequest.searchClientLabel")}
                    placeholder={t("newRequest.searchClientPlaceholder")}
                    size="small"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start">
                            <PersonSearchRoundedIcon color="action" fontSize="small" />
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                      endAdornment: (
                        <>
                          {loadingClients ? (
                            <CircularProgress color="inherit" size={20} sx={{ mr: 1 }} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    <ClientSearchOption
                      client={option}
                      primary={option.name || option.nome}
                      secondary={option.email}
                      fallbackPrimary={t("newRequest.noName")}
                    />
                  </li>
                )}
              />
            ) : (
              <Stack direction="column" spacing={1.5}>
                <TextField
                  label={t("newRequest.clientName")}
                  placeholder={t("newRequest.required")}
                  value={newClientName}
                  onChange={(e) => setNewClientName(e.target.value)}
                  size="small"
                  fullWidth
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonAddRoundedIcon color="action" fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  label={t("newRequest.emailOptional")}
                  type="email"
                  placeholder="exemplo@email.com"
                  value={newClientEmail}
                  onChange={(e) => setNewClientEmail(e.target.value)}
                  size="small"
                  fullWidth
                />
                <TextField
                  label={t("newRequest.phoneOptional")}
                  placeholder="(00) 00000-0000"
                  value={newClientPhone}
                  onChange={(e) => setNewClientPhone(e.target.value)}
                  size="small"
                  fullWidth
                />
              </Stack>
            )}
          </Box>

          {/* Tipo: um único select; ao selecionar, mostram-se os subtipos em cascata */}
          <Box>
            <FormControl fullWidth size="small">
              <InputLabel>{t("newRequest.type")}</InputLabel>
              <Select
                value={typeId}
                label={t("newRequest.type")}
                onChange={(e) => setTypeId(e.target.value)}
              >
                <MenuItem value="">{t("newRequest.typeNone")}</MenuItem>
                {rootTypes.map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    {t.nome ?? t.name ?? `Tipo ${t.id}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Subtipos em cascata — só aparecem quando há um tipo selecionado */}
            {typeId && (
              <Stack spacing={2} sx={{ mt: 2 }}>
                {subtype1Options.length > 0 && (
                  <FormControl fullWidth size="small">
                    <InputLabel>Subtipo 1</InputLabel>
                    <Select
                      value={subtype1}
                      label="Subtipo 1"
                      onChange={(e) => setSubtype1(e.target.value)}
                    >
                      <MenuItem value="">Nenhum</MenuItem>
                      {subtype1Options.map((t) => (
                        <MenuItem key={t.id} value={t.id}>
                          {t.nome ?? t.name ?? `Subtipo ${t.id}`}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                {subtype2Options.length > 0 && (
                  <FormControl fullWidth size="small">
                    <InputLabel>Subtipo 2</InputLabel>
                    <Select
                      value={subtype2}
                      label="Subtipo 2"
                      onChange={(e) => setSubtype2(e.target.value)}
                    >
                      <MenuItem value="">Nenhum</MenuItem>
                      {subtype2Options.map((t) => (
                        <MenuItem key={t.id} value={t.id}>
                          {t.nome ?? t.name ?? `Subtipo ${t.id}`}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                {subtype3Options.length > 0 && (
                  <FormControl fullWidth size="small">
                    <InputLabel>Subtipo 3</InputLabel>
                    <Select
                      value={subtype3}
                      label="Subtipo 3"
                      onChange={(e) => setSubtype3(e.target.value)}
                    >
                      <MenuItem value="">Nenhum</MenuItem>
                      {subtype3Options.map((t) => (
                        <MenuItem key={t.id} value={t.id}>
                          {t.nome ?? t.name ?? `Subtipo ${t.id}`}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </Stack>
            )}
          </Box>

          {/* Opções avançadas: apenas origem e responsável */}
          <Box>
            <Button
              size="small"
              startIcon={
                showAdvanced ? (
                  <ExpandLessRoundedIcon />
                ) : (
                  <ExpandMoreRoundedIcon />
                )
              }
              onClick={() => setShowAdvanced((v) => !v)}
              sx={{ textTransform: "none" }}
            >
              {showAdvanced ? t("newRequest.advancedHide") : t("newRequest.advancedShow")}
            </Button>
            <Collapse in={showAdvanced}>
              <Stack spacing={2} sx={{ mt: 1.5, pl: 0.5 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>{t("newRequest.sourceChannel")}</InputLabel>
                  <Select
                    value={channelId}
                    label={t("newRequest.sourceChannel")}
                    onChange={(e) => setChannelId(e.target.value)}
                  >
                    <MenuItem value="">{t("newRequest.defaultChannel")}</MenuItem>
                    {channels.map((ch) => (
                      <MenuItem key={ch.id} value={ch.id}>
                        {ch.nome ?? ch.name ?? `Canal ${ch.id}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {canCreateWhatsappGroup ? (
                  <Box>
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={createWhatsappGroup}
                          onChange={(e) => {
                            setCreateWhatsappGroup(e.target.checked);
                            setGroupCreateError("");
                            if (!e.target.checked) {
                              setGroupParticipantClients([]);
                              setGroupParticipantSearch("");
                            }
                          }}
                        />
                      }
                      label={t("newRequest.createWhatsappGroup")}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                      {t("newRequest.createWhatsappGroupHint")}
                    </Typography>
                    {createWhatsappGroup ? (
                      <Stack spacing={1.5}>
                        <TextField
                          size="small"
                          label={t("newRequest.groupSubject")}
                          value={groupSubject}
                          onChange={(e) => setGroupSubject(e.target.value)}
                          fullWidth
                        />
                        <WhatsappGroupParticipantsPicker
                          value={groupParticipantClients}
                          onChange={setGroupParticipantClients}
                          options={groupClientOptions}
                          loading={loadingGroupClients}
                          inputValue={groupParticipantSearch}
                          onInputChange={(_, value) => setGroupParticipantSearch(value)}
                          disabled={hasMultipleCompanies && !selectedProjectId}
                          label={t("newRequest.groupParticipants")}
                          placeholder={t("newRequest.groupParticipantsPlaceholder")}
                          helperText={t("newRequest.groupParticipantsHint")}
                          noOptionsText={
                            groupParticipantSearch.trim().length < MIN_CLIENT_SEARCH_LENGTH
                              ? t("newRequest.groupParticipantsPlaceholder")
                              : t("newRequest.groupParticipantsEmpty")
                          }
                          noNameLabel={t("newRequest.noName")}
                        />
                        {groupCreateError ? <Alert severity="error">{groupCreateError}</Alert> : null}
                      </Stack>
                    ) : null}
                  </Box>
                ) : null}
                <FormControl fullWidth size="small">
                  <InputLabel>{t("newRequest.assignee")}</InputLabel>
                  <Select
                    value={
                      assigneeType === "queue"
                        ? "queue"
                        : assigneeType === "agent"
                        ? (assigneeId ? `agent-${assigneeId}` : "queue")
                        : assigneeType === "user"
                        ? (assigneeId ? `user-${assigneeId}` : "queue")
                        : "queue"
                    }
                    label={t("newRequest.assignee")}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === "queue") {
                        setAssigneeType("queue");
                        setAssigneeId("");
                        return;
                      }
                      if (String(v).startsWith("agent-")) {
                        setAssigneeType("agent");
                        setAssigneeId(String(v).replace("agent-", ""));
                        return;
                      }
                      if (String(v).startsWith("user-")) {
                        setAssigneeType("user");
                        setAssigneeId(String(v).replace("user-", ""));
                      }
                    }}
                    renderValue={(v) => {
                      if (v === "queue") return t("newRequest.queue");
                      if (String(v).startsWith("agent-")) {
                        const id = String(v).replace("agent-", "");
                        const a = agents.find((x) => String(x.id ?? x.id_agente) === id);
                        return a ? (a.name ?? a.nome ?? `Agente ${id}`) : `Agente ${id}`;
                      }
                      const id = String(v).replace("user-", "");
                      const u = users.find((x) => String(x.id) === id);
                      const nome = u ? (u.nome ?? u.name ?? `Usuário ${id}`) : `Usuário ${id}`;
                      const suffix = Number(id) === Number(currentUserId) ? t("newRequest.you") : (u && isChatBotUser(u) ? " (ChatBot)" : "");
                      return nome + suffix;
                    }}
                  >
                    <MenuItem value="queue">{t("newRequest.queue")}</MenuItem>
                    {humanUsers.length > 0 && [
                      <MenuItem key="div-human" disabled sx={{ opacity: 0.7, fontSize: "0.75rem" }}>— {t("newRequest.usersDivider")} —</MenuItem>,
                      ...humanUsers.map((u) => (
                        <MenuItem key={`u-${u.id}`} value={`user-${u.id}`}>
                          {u.nome ?? u.name ?? `Usuário ${u.id}`}
                          {Number(u.id) === Number(currentUserId) ? t("newRequest.you") : ""}
                        </MenuItem>
                      )),
                    ]}
                    {chatbotUsers.length > 0 && [
                      <MenuItem key="div-chatbot" disabled sx={{ opacity: 0.7, fontSize: "0.75rem" }}>— ChatBots —</MenuItem>,
                      ...chatbotUsers.map((u) => (
                        <MenuItem key={`cb-${u.id}`} value={`user-${u.id}`}>
                          {u.nome ?? u.name ?? `ChatBot ${u.id}`} (ChatBot)
                        </MenuItem>
                      )),
                    ]}
                    {agents.length > 0 && [
                      <MenuItem key="div-agent" disabled sx={{ opacity: 0.7, fontSize: "0.75rem" }}>— {t("newRequest.aiAgentsDivider")} —</MenuItem>,
                      ...agents.map((a) => {
                        const id = a.id ?? a.id_agente ?? a.agentId;
                        return (
                          <MenuItem key={`a-${id}`} value={`agent-${id}`}>
                            {a.name ?? a.nome ?? `Agente ${id}`}
                          </MenuItem>
                        );
                      }),
                    ]}
                  </Select>
                </FormControl>
              </Stack>
            </Collapse>
          </Box>

          <Divider />

          <TextField
            label={t("newRequest.initialMessage")}
            placeholder={t("newRequest.initialMessagePlaceholder")}
            multiline
            minRows={2}
            value={initialMessage}
            onChange={(e) => setInitialMessage(e.target.value)}
            fullWidth
            size="small"
          />

          <Button
            variant="contained"
            size="large"
            fullWidth
            onClick={handleSubmit}
            disabled={!canSubmit() || isCheckingOpenByPhone}
            startIcon={
              isCreating || isCheckingOpenByPhone || creatingGroup ? (
                <CircularProgress size={20} color="inherit" />
              ) : null
            }
          >
            {isCreating || isCheckingOpenByPhone || creatingGroup
              ? t("newRequest.creating")
              : createWhatsappGroup && canCreateWhatsappGroup
                ? t("newRequest.createGroupAndRequest")
                : t("newRequest.createRequest")}
          </Button>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isCreating || creatingGroup}>
          {t("newRequest.cancel")}
        </Button>
      </DialogActions>
      {/* Aviso de solicitações abertas por telefone */}
      <Dialog
        open={openPhoneWarning}
        onClose={() => {
          if (!isCreating) {
            setOpenPhoneWarning(false);
          }
        }}
      >
        <DialogTitle>
          {t("newRequest.phoneWarnTitle")}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            {t("newRequest.phoneWarnIntro", { count: openPhoneWarningItems.length })}
            <strong>{t("newRequest.pending")}</strong>
            {t("newRequest.or")}
            <strong>{t("newRequest.inProgress")}</strong>.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t("newRequest.phoneWarnHint")}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenPhoneWarning(false);
              setPendingCreatePayload(null);
            }}
            disabled={isCreating}
          >
            {t("newRequest.cancel")}
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={async () => {
              const payload = pendingCreatePayload || buildPayload();
              setOpenPhoneWarning(false);
              setPendingCreatePayload(null);
              await doCreateConversation(payload);
            }}
            disabled={isCreating}
          >
            {t("newRequest.createAnyway")}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default NewRequestModal;
